// Neighbour-aware tile picker. Ported from WinBolo's `screencalc.c` — for a
// given terrain tile and its 8 surrounding neighbours, return the (sx, sy)
// in the WinBolo tile sheet that gives a smooth corner/edge/junction look.
//
// Six terrain types have blending logic: BUILDING, ROAD, RIVER, DEEP_SEA,
// FOREST, BOAT. Others (GRASS, SWAMP, RUBBLE, CRATER) always render their
// solid sprite — that matches WinBolo's behaviour exactly (crater has dead
// variant code in the original; we preserve that intent here).

import { Terrain, TERRAIN_MASK } from '@bolo2k/shared';

export interface TilePos {
  sx: number;
  sy: number;
}

// --- Tile-sheet position table -------------------------------------------
// Mirrors WinBolo's `tiles.h` constants. Pixel coords in the 496×176 sheet.

// Roads + road-water bridges
const ROAD_HORZ: TilePos = { sx: 0, sy: 0 };
const ROAD_VERT: TilePos = { sx: 16, sy: 0 };
const ROAD_CORNER1: TilePos = { sx: 32, sy: 0 };
const ROAD_CORNER2: TilePos = { sx: 48, sy: 0 };
const ROAD_CORNER3: TilePos = { sx: 64, sy: 0 };
const ROAD_CORNER4: TilePos = { sx: 80, sy: 0 };
const ROAD_CORNER5: TilePos = { sx: 96, sy: 0 };
const ROAD_CORNER6: TilePos = { sx: 112, sy: 0 };
const ROAD_CORNER7: TilePos = { sx: 128, sy: 0 };
const ROAD_CORNER8: TilePos = { sx: 144, sy: 0 };
const ROAD_SOLID: TilePos = { sx: 160, sy: 0 };
const ROAD_CROSS: TilePos = { sx: 176, sy: 0 };
const ROAD_T1: TilePos = { sx: 192, sy: 0 };
const ROAD_T2: TilePos = { sx: 208, sy: 0 };
const ROAD_T3: TilePos = { sx: 224, sy: 0 };
const ROAD_T4: TilePos = { sx: 240, sy: 0 };
const ROAD_WATER1: TilePos = { sx: 256, sy: 0 };
const ROAD_WATER2: TilePos = { sx: 272, sy: 0 };
const ROAD_WATER3: TilePos = { sx: 288, sy: 0 };
const ROAD_WATER4: TilePos = { sx: 304, sy: 0 };
const ROAD_WATER5: TilePos = { sx: 320, sy: 0 };
const ROAD_WATER6: TilePos = { sx: 336, sy: 0 };
const ROAD_WATER7: TilePos = { sx: 352, sy: 0 };
const ROAD_WATER8: TilePos = { sx: 368, sy: 0 };
const ROAD_WATER9: TilePos = { sx: 384, sy: 0 };
const ROAD_WATER10: TilePos = { sx: 400, sy: 0 };
const ROAD_WATER11: TilePos = { sx: 416, sy: 0 };
const ROAD_SIDE1: TilePos = { sx: 432, sy: 0 };
const ROAD_SIDE2: TilePos = { sx: 448, sy: 0 };
const ROAD_SIDE3: TilePos = { sx: 464, sy: 0 };
const ROAD_SIDE4: TilePos = { sx: 480, sy: 0 };

// Deep sea
const DEEP_SEA_SOLID: TilePos = { sx: 0, sy: 16 };
const DEEP_SEA_CORN1: TilePos = { sx: 160, sy: 48 };
const DEEP_SEA_CORN2: TilePos = { sx: 176, sy: 48 };
const DEEP_SEA_CORN3: TilePos = { sx: 192, sy: 48 };
const DEEP_SEA_CORN4: TilePos = { sx: 208, sy: 48 };
const DEEP_SEA_SIDE1: TilePos = { sx: 224, sy: 48 };
const DEEP_SEA_SIDE2: TilePos = { sx: 240, sy: 48 };
const DEEP_SEA_SIDE3: TilePos = { sx: 256, sy: 48 };
const DEEP_SEA_SIDE4: TilePos = { sx: 272, sy: 48 };

// Boat (river-with-bridge tile, NOT the ferry-boat entity sprite)
const BOAT_0: TilePos = { sx: 288, sy: 48 };
const BOAT_1: TilePos = { sx: 176, sy: 96 };
const BOAT_2: TilePos = { sx: 192, sy: 96 };
const BOAT_3: TilePos = { sx: 208, sy: 96 };
const BOAT_4: TilePos = { sx: 224, sy: 96 };
const BOAT_5: TilePos = { sx: 240, sy: 96 };
const BOAT_6: TilePos = { sx: 256, sy: 96 };
const BOAT_7: TilePos = { sx: 272, sy: 96 };

// Solid uniform tiles
const RIVER_SOLID: TilePos = { sx: 16, sy: 16 };
const GRASS_TILE: TilePos = { sx: 32, sy: 16 };
const FOREST_TILE: TilePos = { sx: 48, sy: 16 };
const RUBBLE_TILE: TilePos = { sx: 64, sy: 16 };
const CRATER_TILE: TilePos = { sx: 80, sy: 16 };
const SWAMP_TILE: TilePos = { sx: 112, sy: 16 };
const SHOT_BUILDING: TilePos = { sx: 128, sy: 16 };

// Forest variants (in WinBolo, these positions are shared by crater variants
// — but crater's variant code is dead, so only forest uses them).
const FOREST_SINGLE: TilePos = { sx: 128, sy: 144 };
const FOREST_BR: TilePos = { sx: 144, sy: 144 };
const FOREST_BL: TilePos = { sx: 160, sy: 144 };
const FOREST_AL: TilePos = { sx: 176, sy: 144 };
const FOREST_AR: TilePos = { sx: 192, sy: 144 };
const FOREST_RIGHT: TilePos = { sx: 208, sy: 144 };
const FOREST_LEFT: TilePos = { sx: 224, sy: 144 };
const FOREST_BELOW: TilePos = { sx: 240, sy: 144 };
const FOREST_ABOVE: TilePos = { sx: 256, sy: 144 };

// Building (the most complex picker; many variants)
const BUILD_SINGLE: TilePos = { sx: 96, sy: 16 };
const BUILD_HORZ: TilePos = { sx: 176, sy: 16 };
const BUILD_VERT: TilePos = { sx: 192, sy: 16 };
const BUILD_HORZEND1: TilePos = { sx: 208, sy: 16 };
const BUILD_HORZEND2: TilePos = { sx: 224, sy: 16 };
const BUILD_VERTEND1: TilePos = { sx: 240, sy: 16 };
const BUILD_VERTEND2: TilePos = { sx: 256, sy: 16 };
const BUILD_SOLID: TilePos = { sx: 272, sy: 16 };
const BUILD_CORNER1: TilePos = { sx: 288, sy: 16 };
const BUILD_CORNER2: TilePos = { sx: 304, sy: 16 };
const BUILD_CORNER3: TilePos = { sx: 320, sy: 16 };
const BUILD_CORNER4: TilePos = { sx: 336, sy: 16 };
const BUILD_L1: TilePos = { sx: 352, sy: 16 };
const BUILD_L2: TilePos = { sx: 368, sy: 16 };
const BUILD_L3: TilePos = { sx: 384, sy: 16 };
const BUILD_L4: TilePos = { sx: 400, sy: 16 };
const BUILD_T1: TilePos = { sx: 416, sy: 16 };
const BUILD_T2: TilePos = { sx: 432, sy: 16 };
const BUILD_T3: TilePos = { sx: 448, sy: 16 };
const BUILD_T4: TilePos = { sx: 464, sy: 16 };
const BUILD_CROSS: TilePos = { sx: 480, sy: 16 };
const BUILD_SIDE1: TilePos = { sx: 0, sy: 32 };
const BUILD_SIDE2: TilePos = { sx: 16, sy: 32 };
const BUILD_SIDE3: TilePos = { sx: 32, sy: 32 };
const BUILD_SIDE4: TilePos = { sx: 48, sy: 32 };
const BUILD_SIDECORN1: TilePos = { sx: 64, sy: 32 };
const BUILD_SIDECORN2: TilePos = { sx: 80, sy: 32 };
const BUILD_SIDECORN3: TilePos = { sx: 96, sy: 32 };
const BUILD_SIDECORN4: TilePos = { sx: 112, sy: 32 };
const BUILD_SIDECORN5: TilePos = { sx: 128, sy: 32 };
const BUILD_SIDECORN6: TilePos = { sx: 144, sy: 32 };
const BUILD_SIDECORN7: TilePos = { sx: 160, sy: 32 };
const BUILD_SIDECORN8: TilePos = { sx: 176, sy: 32 };
const BUILD_SIDECORN9: TilePos = { sx: 192, sy: 32 };
const BUILD_SIDECORN10: TilePos = { sx: 208, sy: 32 };
const BUILD_SIDECORN11: TilePos = { sx: 224, sy: 32 };
const BUILD_SIDECORN12: TilePos = { sx: 240, sy: 32 };
const BUILD_SIDECORN13: TilePos = { sx: 256, sy: 32 };
const BUILD_SIDECORN14: TilePos = { sx: 272, sy: 32 };
const BUILD_SIDECORN15: TilePos = { sx: 288, sy: 32 };
const BUILD_SIDECORN16: TilePos = { sx: 304, sy: 32 };
const BUILD_TWIST1: TilePos = { sx: 320, sy: 32 };
const BUILD_TWIST2: TilePos = { sx: 336, sy: 32 };
const BUILD_MOST1: TilePos = { sx: 352, sy: 32 };
const BUILD_MOST2: TilePos = { sx: 368, sy: 32 };
const BUILD_MOST3: TilePos = { sx: 384, sy: 32 };
const BUILD_MOST4: TilePos = { sx: 400, sy: 32 };

// River variants
const RIVER_END1: TilePos = { sx: 416, sy: 32 };
const RIVER_END2: TilePos = { sx: 432, sy: 32 };
const RIVER_END3: TilePos = { sx: 448, sy: 32 };
const RIVER_END4: TilePos = { sx: 464, sy: 32 };
const RIVER_SURROUND: TilePos = { sx: 480, sy: 32 };
const RIVER_SIDE1: TilePos = { sx: 0, sy: 48 };
const RIVER_SIDE2: TilePos = { sx: 16, sy: 48 };
const RIVER_ONESIDE1: TilePos = { sx: 32, sy: 48 };
const RIVER_ONESIDE2: TilePos = { sx: 48, sy: 48 };
const RIVER_ONESIDE3: TilePos = { sx: 64, sy: 48 };
const RIVER_ONESIDE4: TilePos = { sx: 80, sy: 48 };
const RIVER_CORN1: TilePos = { sx: 96, sy: 48 };
const RIVER_CORN2: TilePos = { sx: 112, sy: 48 };
const RIVER_CORN3: TilePos = { sx: 128, sy: 48 };
const RIVER_CORN4: TilePos = { sx: 144, sy: 48 };

// --- Pickers --------------------------------------------------------------
//
// Each picker takes its 8 neighbours' terrain codes (low-4-bit, no mine bit)
// and returns a TilePos. They mirror WinBolo's screencalc.c functions
// verbatim, including their pre-pass aliasing.

// Helper: alias a neighbour. Used by the pre-pass blocks below.
function aliasIf(value: number, from: number, to: number): number {
  return value === from ? to : value;
}

interface Neighbours {
  aboveLeft: number;
  above: number;
  aboveRight: number;
  left: number;
  right: number;
  belowLeft: number;
  below: number;
  belowRight: number;
}

function pickRoad(n: Neighbours): TilePos {
  // Pre-pass: DEEP_SEA / BOAT → RIVER
  let { aboveLeft, above, aboveRight, left, right, belowLeft, below, belowRight } = n;
  for (const _ of [0]) {
    void _;
    aboveLeft = aliasIf(aliasIf(aboveLeft, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    above = aliasIf(aliasIf(above, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    aboveRight = aliasIf(aliasIf(aboveRight, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    left = aliasIf(aliasIf(left, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    right = aliasIf(aliasIf(right, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    belowLeft = aliasIf(aliasIf(belowLeft, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    below = aliasIf(aliasIf(below, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
    belowRight = aliasIf(aliasIf(belowRight, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
  }
  const R = Terrain.Road;
  const W = Terrain.River;

  if (aboveLeft !== R && above === R && aboveRight !== R && left === R && right === R && belowLeft !== R && below === R && belowRight !== R) return ROAD_CROSS;
  if (above === R && left === R && right === R && below === R) return ROAD_SOLID;
  if (left === W && right === W && above === W && below === W) return ROAD_WATER11;
  if (right === R && below === R && left === W && above === W) return ROAD_WATER5;
  if (left === R && below === R && right === W && above === W) return ROAD_WATER6;
  if (above === R && left === R && below === W && right === W) return ROAD_WATER7;
  if (right === R && above === R && left === W && below === W) return ROAD_WATER8;
  if (above === W && below === W) return ROAD_WATER9;
  if (left === W && right === W) return ROAD_WATER10;
  if (above === W && below === R) return ROAD_WATER1;
  if (right === W && left === R) return ROAD_WATER2;
  if (below === W && above === R) return ROAD_WATER3;
  if (left === W && right === R) return ROAD_WATER4;
  if (right === R && below === R && above === R && (aboveRight === R || belowRight === R)) return ROAD_SIDE1;
  if (left === R && right === R && below === R && (belowLeft === R || belowRight === R)) return ROAD_SIDE2;
  if (left === R && above === R && below === R && (belowLeft === R || aboveLeft === R)) return ROAD_SIDE3;
  if (left === R && right === R && above === R && (aboveRight === R || aboveLeft === R)) return ROAD_SIDE4;
  if (left === R && right === R && below === R) return ROAD_T1;
  if (left === R && above === R && below === R) return ROAD_T2;
  if (left === R && right === R && above === R) return ROAD_T3;
  if (right === R && above === R && below === R) return ROAD_T4;
  if (below === R && right === R && belowRight === R) return ROAD_CORNER5;
  if (below === R && left === R && belowLeft === R) return ROAD_CORNER6;
  if (above === R && left === R && aboveLeft === R) return ROAD_CORNER7;
  if (above === R && right === R && aboveRight === R) return ROAD_CORNER8;
  if (below === R && right === R) return ROAD_CORNER1;
  if (below === R && left === R) return ROAD_CORNER2;
  if (above === R && left === R) return ROAD_CORNER3;
  if (above === R && right === R) return ROAD_CORNER4;
  if (right === R || left === R) return ROAD_HORZ;
  if (above === R || below === R) return ROAD_VERT;
  return ROAD_SOLID;
}

function pickBoat(n: Neighbours): TilePos {
  // Pre-pass: DEEP_SEA / BOAT → RIVER (DEEP_SEA included so a boat tile
  // surrounded by deep sea picks the same variant as one surrounded by river)
  let { above, left, right, below } = n;
  above = aliasIf(aliasIf(above, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
  left = aliasIf(aliasIf(left, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
  right = aliasIf(aliasIf(right, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
  below = aliasIf(aliasIf(below, Terrain.DeepSea, Terrain.River), Terrain.Boat, Terrain.River);
  const W = Terrain.River;

  if (above !== W && left !== W) return BOAT_5;
  if (above !== W && right !== W) return BOAT_6;
  if (below !== W && right !== W) return BOAT_7;
  if (below !== W && left !== W) return BOAT_4;
  if (left !== W) return BOAT_2;
  if (right !== W) return BOAT_3;
  if (below !== W) return BOAT_0;
  return BOAT_1;
}

function pickBuilding(n: Neighbours): TilePos {
  // Pre-pass: HALFBUILDING → BUILDING
  const aliasB = (v: number) => (v === Terrain.HalfBuilding ? Terrain.Building : v);
  const aboveLeft = aliasB(n.aboveLeft);
  const above = aliasB(n.above);
  const aboveRight = aliasB(n.aboveRight);
  const left = aliasB(n.left);
  const right = aliasB(n.right);
  const belowLeft = aliasB(n.belowLeft);
  const below = aliasB(n.below);
  const belowRight = aliasB(n.belowRight);
  const B = Terrain.Building;

  if (aboveLeft === B && above === B && aboveRight === B && left === B && right === B && belowLeft === B && below === B && belowRight === B) return BUILD_SOLID;
  if (right === B && above === B && below === B && left === B && aboveRight !== B && aboveLeft !== B && belowRight !== B && belowLeft !== B) return BUILD_CROSS;
  if (right === B && above === B && below === B && left === B && aboveRight !== B && aboveLeft !== B && belowRight !== B && belowLeft === B) return BUILD_MOST1;
  if (right === B && above === B && below === B && left === B && aboveRight !== B && aboveLeft === B && belowRight !== B && belowLeft !== B) return BUILD_MOST2;
  if (right === B && above === B && below === B && left === B && aboveRight !== B && aboveLeft !== B && belowRight === B && belowLeft !== B) return BUILD_MOST3;
  if (right === B && above === B && below === B && left === B && aboveRight === B && aboveLeft !== B && belowRight !== B && belowLeft !== B) return BUILD_MOST4;
  if (aboveLeft === B && above === B && left === B && right === B && belowLeft === B && below === B && belowRight === B) return BUILD_SIDECORN13;
  if (above === B && aboveRight === B && left === B && right === B && belowLeft === B && below === B && belowRight === B) return BUILD_SIDECORN14;
  if (aboveLeft === B && above === B && aboveRight === B && left === B && right === B && belowLeft === B && below === B) return BUILD_SIDECORN15;
  if (aboveLeft === B && above === B && aboveRight === B && left === B && right === B && below === B && belowRight === B) return BUILD_SIDECORN16;
  if (left === B && right === B && above === B && below === B && aboveRight === B && belowLeft === B && aboveLeft !== B && belowRight !== B) return BUILD_TWIST1;
  if (left === B && right === B && above === B && below === B && belowRight === B && aboveLeft === B && aboveRight !== B && belowLeft !== B) return BUILD_TWIST2;
  if (above === B && left === B && right === B && below === B && belowRight === B && aboveRight === B) return BUILD_SIDECORN5;
  if (above === B && left === B && right === B && below === B && belowLeft === B && aboveLeft === B) return BUILD_SIDECORN6;
  if (above === B && left === B && right === B && below === B && belowLeft === B && belowRight === B) return BUILD_SIDECORN7;
  if (above === B && left === B && right === B && below === B && aboveLeft === B && aboveRight === B) return BUILD_SIDECORN8;
  if (above === B && below === B && left === B && right !== B && belowLeft === B && aboveLeft !== B) return BUILD_SIDECORN9;
  if (above === B && below === B && right === B && belowRight === B && left !== B && aboveRight !== B) return BUILD_SIDECORN10;
  if (above === B && below === B && right === B && aboveRight === B && belowRight !== B) return BUILD_SIDECORN11;
  if (above === B && below === B && left === B && aboveLeft === B && belowLeft !== B) return BUILD_SIDECORN12;
  if (right === B && above === B && left === B && below !== B && aboveLeft !== B && aboveRight !== B) return BUILD_T1;
  if (right === B && below === B && left === B && belowLeft !== B && belowRight !== B) return BUILD_T2;
  if (right === B && above === B && below === B && aboveRight !== B && belowRight !== B) return BUILD_T3;
  if (below === B && above === B && left === B && aboveLeft !== B && belowLeft !== B) return BUILD_T4;
  if (left === B && right === B && above === B && aboveRight === B && aboveLeft !== B) return BUILD_SIDECORN1;
  if (left === B && right === B && above === B && aboveLeft === B && aboveRight !== B) return BUILD_SIDECORN2;
  if (left === B && right === B && below === B && belowLeft === B && belowRight !== B) return BUILD_SIDECORN3;
  if (left === B && right === B && below === B && above !== B && belowRight === B && belowLeft !== B) return BUILD_SIDECORN4;
  if (right === B && above === B && below === B) return BUILD_SIDE1;
  if (left === B && above === B && below === B) return BUILD_SIDE2;
  if (right === B && left === B && below === B) return BUILD_SIDE3;
  if (right === B && above === B && left === B) return BUILD_SIDE4;
  if (right === B && below === B && belowRight === B) return BUILD_CORNER1;
  if (left === B && below === B && belowLeft === B) return BUILD_CORNER2;
  if (right === B && above === B && aboveRight === B) return BUILD_CORNER3;
  if (left === B && above === B && aboveLeft === B) return BUILD_CORNER4;
  if (right === B && below === B) return BUILD_L1;
  if (left === B && below === B) return BUILD_L2;
  if (right === B && above === B) return BUILD_L3;
  if (left === B && above === B) return BUILD_L4;
  if (left === B && right === B) return BUILD_HORZ;
  if (above === B && below === B) return BUILD_VERT;
  if (right === B) return BUILD_HORZEND1;
  if (left === B) return BUILD_HORZEND2;
  if (below === B) return BUILD_VERTEND1;
  if (above === B) return BUILD_VERTEND2;
  return BUILD_SINGLE;
}

function pickRiver(n: Neighbours): TilePos {
  // Pre-pass: DEEP_SEA / BOAT → RIVER (so deep sea is "water-equivalent" for
  // adjacency)
  const alias = (v: number) =>
    v === Terrain.DeepSea || v === Terrain.Boat ? Terrain.River : v;
  const above = alias(n.above);
  const left = alias(n.left);
  const right = alias(n.right);
  const below = alias(n.below);
  const W = Terrain.River;
  const R = Terrain.Road;
  const isWaterOrRoad = (t: number) => t === W || t === R;

  if (!isWaterOrRoad(above) && !isWaterOrRoad(below) && !isWaterOrRoad(right) && !isWaterOrRoad(left)) return RIVER_SURROUND;
  if (!isWaterOrRoad(above) && !isWaterOrRoad(below) && right === W && !isWaterOrRoad(left)) return RIVER_END1;
  if (!isWaterOrRoad(above) && !isWaterOrRoad(below) && !isWaterOrRoad(right) && left === W) return RIVER_END2;
  if (!isWaterOrRoad(above) && below === W && !isWaterOrRoad(right) && !isWaterOrRoad(left)) return RIVER_END3;
  if (above === W && !isWaterOrRoad(below) && !isWaterOrRoad(right) && !isWaterOrRoad(left)) return RIVER_END4;
  if (!isWaterOrRoad(above) && !isWaterOrRoad(left)) return RIVER_CORN1;
  if (!isWaterOrRoad(above) && !isWaterOrRoad(right)) return RIVER_CORN2;
  if (!isWaterOrRoad(below) && !isWaterOrRoad(left)) return RIVER_CORN3;
  if (!isWaterOrRoad(below) && !isWaterOrRoad(right)) return RIVER_CORN4;
  // WinBolo had a typo here — repeated `below` check that was almost
  // certainly meant to test both vertical neighbours (a river flowing
  // horizontally with land above and below). We fix it to match intent.
  if (!isWaterOrRoad(above) && !isWaterOrRoad(below)) return RIVER_SIDE1;
  if (!isWaterOrRoad(left) && !isWaterOrRoad(right)) return RIVER_SIDE2;
  if (!isWaterOrRoad(left)) return RIVER_ONESIDE1;
  if (!isWaterOrRoad(below)) return RIVER_ONESIDE2;
  if (!isWaterOrRoad(right)) return RIVER_ONESIDE3;
  if (!isWaterOrRoad(above)) return RIVER_ONESIDE4;
  return RIVER_SOLID;
}

function pickDeepSea(n: Neighbours): TilePos {
  // Pre-pass: BOAT → RIVER. (DeepSea is *not* aliased — it's the matching target.)
  const alias = (v: number) => (v === Terrain.Boat ? Terrain.River : v);
  const aboveLeft = alias(n.aboveLeft);
  const above = alias(n.above);
  const aboveRight = alias(n.aboveRight);
  const left = alias(n.left);
  const right = alias(n.right);
  const belowLeft = alias(n.belowLeft);
  const below = alias(n.below);
  const belowRight = alias(n.belowRight);
  const D = Terrain.DeepSea;
  const W = Terrain.River;

  if (aboveLeft !== D && above !== D && left !== D && right === D && below === D) return DEEP_SEA_CORN1;
  if (aboveRight !== D && above !== D && right !== D && left === D && below === D) return DEEP_SEA_CORN2;
  if (belowRight !== D && below !== D && right !== D && left === D && above === D) return DEEP_SEA_CORN4;
  if (belowLeft !== D && below !== D && left !== D && right === D && above === D) return DEEP_SEA_CORN3;
  if (left === W && right === D) return DEEP_SEA_SIDE1;
  if (below === W && above === D) return DEEP_SEA_SIDE2;
  if (above === W && below === D) return DEEP_SEA_SIDE3;
  if (right === W && left === D) return DEEP_SEA_SIDE4;
  return DEEP_SEA_SOLID;
}

function pickForest(n: Neighbours): TilePos {
  const F = Terrain.Forest;
  const { above, left, right, below } = n;
  if (above !== F && left !== F && right === F && below === F) return FOREST_BR;
  if (above !== F && left === F && right !== F && below === F) return FOREST_BL;
  if (above === F && left === F && right !== F && below !== F) return FOREST_AL;
  if (above === F && left !== F && right === F && below !== F) return FOREST_AR;
  if (above === F && left !== F && right !== F && below !== F) return FOREST_ABOVE;
  if (above !== F && left !== F && right !== F && below === F) return FOREST_BELOW;
  if (above !== F && left === F && right !== F && below !== F) return FOREST_LEFT;
  if (above !== F && left !== F && right === F && below !== F) return FOREST_RIGHT;
  if (above !== F && below !== F && left !== F && right !== F) return FOREST_SINGLE;
  return FOREST_TILE;
}

// --- Master picker --------------------------------------------------------

export function pickTilePosition(terrainCode: number, n: Neighbours): TilePos {
  switch (terrainCode) {
    case Terrain.Building:
      return pickBuilding(n);
    case Terrain.HalfBuilding:
      return SHOT_BUILDING;
    case Terrain.River:
      return pickRiver(n);
    case Terrain.Boat:
      return pickBoat(n);
    case Terrain.DeepSea:
      return pickDeepSea(n);
    case Terrain.Forest:
      return pickForest(n);
    case Terrain.Road:
      return pickRoad(n);
    case Terrain.Grass:
      return GRASS_TILE;
    case Terrain.Swamp:
      return SWAMP_TILE;
    case Terrain.Rubble:
      return RUBBLE_TILE;
    case Terrain.Crater:
      return CRATER_TILE; // WinBolo's crater variant logic is dead code
    case Terrain.RefBase:
      return GRASS_TILE; // tile under the base (entity drawn on top)
    case Terrain.Pillbox:
      return GRASS_TILE; // tile under the pillbox (entity drawn on top)
    default:
      return GRASS_TILE;
  }
}

// Helper: read a tile's neighbours from a 256-wide terrain buffer. Out-of-
// bounds cells are treated as DeepSea (matching the world's edge behaviour).
const MAP_W = 256;

export function readNeighbours(terrain: Uint8Array, x: number, y: number): Neighbours {
  const at = (xx: number, yy: number): number => {
    if (xx < 0 || xx >= MAP_W || yy < 0 || yy >= MAP_W) return Terrain.DeepSea;
    return terrain[yy * MAP_W + xx]! & TERRAIN_MASK;
  };
  return {
    aboveLeft: at(x - 1, y - 1),
    above: at(x, y - 1),
    aboveRight: at(x + 1, y - 1),
    left: at(x - 1, y),
    right: at(x + 1, y),
    belowLeft: at(x - 1, y + 1),
    below: at(x, y + 1),
    belowRight: at(x + 1, y + 1),
  };
}
